import getConfig from 'next/config';
import { useEffect, useMemo } from 'react';

import {
  RemoteRouteStatus,
  ROUTE_TYPE_TYPE,
  useAllRegionMegaMenuQuery,
  useRemoteRouteStore,
  useRouter,
  useServiceAppState,
  useServiceMetaQuery,
} from '@kc-console/context';
import { matchPath, STATUS_CODE } from '@kc-console/core';

const useServiceRouteEffect = () => {
  const { setClientAppStatus } = useServiceAppState();
  const setRoute = useRemoteRouteStore(s => s.setRoute);
  const setStatus = useRemoteRouteStore(s => s.setStatus);

  const router = useRouter();

  const {
    state: { serviceKeyword },
  } = useServiceAppState();

  const { data: serviceMeta, isError: isErrorServiceMeta } = useServiceMetaQuery();
  const { routes = [] } = serviceMeta || {};

  const routePath = useMemo(() => {
    const pathname = router.asPath.split('?')[0];
    return pathname.replace(`${serviceKeyword}/`, '');
  }, [router.asPath, serviceKeyword]);

  const matchedRoute = useMemo(() => {
    return routes?.find(route => matchPath(routePath, route.path));
  }, [routes, routePath]);

  const existsInCurrentRegion = useMemo(
    () => !isErrorServiceMeta && Boolean(serviceMeta),
    [isErrorServiceMeta, serviceMeta],
  );

  const shouldCheckAllRegions = Boolean(serviceKeyword) && !existsInCurrentRegion;
  const { data: allRegionCategories, isPending: isPendingAllRegion } = useAllRegionMegaMenuQuery({
    enabled: shouldCheckAllRegions,
  });

  // NOTE: disabled 쿼리는 isPending이 계속 true이므로, 조회를 실제로 걸었을 때만 보류로 판정한다.
  const isResolvingNotFound = shouldCheckAllRegions && isPendingAllRegion;

  /**
   * 서비스 미발견 시 표시할 상태 코드.
   * @description
   * - null: 서비스가 현재 리전에 존재하거나 아직 조회 중. 서비스가 존재하는 한 항상 null로
   *   고정되어, 같은 쿼리를 공유하는 다른 소비처의 조회가 아래 effect를 재실행시키지 않는다.
   * - FORBIDDEN_REGION: 다른 리전에는 존재.
   * - NOT_FOUND_SERVICE: 어디에도 없거나 조회 실패.
   */
  const resolvedNotFoundStatus = useMemo(() => {
    if (existsInCurrentRegion) return null;
    if (isPendingAllRegion) return null;

    const existsInAnyRegion = (allRegionCategories ?? []).some(category =>
      category.services.some(service => service.keyword === serviceKeyword),
    );
    return existsInAnyRegion ? STATUS_CODE.FORBIDDEN_REGION : STATUS_CODE.NOT_FOUND_SERVICE;
  }, [existsInCurrentRegion, isPendingAllRegion, allRegionCategories, serviceKeyword]);

  useEffect(() => {
    // 이전 서비스의 클라이언트 판정이 보류 중 화면에 남지 않도록 OK로 초기화한다.
    // (effect 재실행 = deps 변화 = 새 판정 상황. 동일 서비스 재클릭은 재실행되지 않아 기존 판정이 유지된다)
    if (isResolvingNotFound) {
      setStatus(RemoteRouteStatus.Idle);
      setClientAppStatus(STATUS_CODE.OK);
      return;
    }

    if (resolvedNotFoundStatus !== null) {
      setStatus(RemoteRouteStatus.Idle);
      setClientAppStatus(resolvedNotFoundStatus);
      return;
    }

    /**
     * 로컬 개발 편의를 위한 스킵 코드.
     * 로컬에서 서비스 개발 시 serviceMeta.route를 등록하지 않고 개발할 수 있도록 가드를 통과시키는 설정.
     */
    if (process.env.NODE_ENV === 'development' && !getConfig()?.publicRuntimeConfig?.isBaguni) {
      setStatus(RemoteRouteStatus.Idle);
      if (matchedRoute) setRoute(matchedRoute);
      return;
    }

    if (!matchedRoute) {
      setStatus(RemoteRouteStatus.Idle);
      setClientAppStatus(STATUS_CODE.NOT_FOUND_ROUTE);
      return;
    }

    // 이전 클라이언트 판정(4033/4041/4042 등)을 OK로 해소한다.
    setClientAppStatus(STATUS_CODE.OK);

    if (matchedRoute.routeType !== ROUTE_TYPE_TYPE.REDIRECT) {
      setStatus(RemoteRouteStatus.RouteLoading);
      setRoute(matchedRoute);
    }
    // NOTE: serviceKeyword는 effect 본문에서 직접 쓰지 않지만, 서비스 전환 시 재판정을 보장하는 방어적 dep이다.
  }, [
    serviceKeyword,
    isResolvingNotFound,
    resolvedNotFoundStatus,
    matchedRoute,
    setClientAppStatus,
    setRoute,
    setStatus,
  ]);
};

export default useServiceRouteEffect;
